import React, { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm, useFormState } from 'react-hook-form';
import { AppFormProvider } from '@cp/utils/form';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import {
  FormField as ExternalFields,
  __experimentalFormField as FormField,
  withChangeableValue,
  withErrorTooltip,
  withTransformer,
} from '@cp/ds/src/components/form';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { cx } from '@emotion/css';
import { ClientType } from '@common/model/client/clientType';
import { CLIENT_TYPE_TO_CLIENT_TYPE_NAME, EButtonName, EPageName, useTracking, withTrackClick } from 'src/shared/tracking';
import { _experimentalComponentBuilder as componentBuilder } from 'src/shared/componentBuilder';
import { withInputMask } from '@cp/ds/src/hooks';
import { TextInput } from '@cp/ds/src/components/form/components/field/text';
import { DateFormat } from '@common/const/date';
import { DateTransformer, StringCase, StringTransformer } from '@cp/shared/form';
import { StepFooter } from '../stepFooter';
import { StepContainer } from '../StepContainer';
import { acquaintancePopupVisible, aquaintanceSubmit, useErrorHandler, wrapFieldName } from '../../lib';
import { StepWrapper } from '../stepWrapper';
import { PromoAtRegistration } from '../promoAtRegistration';
import { ClientRegistrationStepProps } from './model';

const SubmitButton = componentBuilder(Button)
  .composeWithArgs(withTrackClick, aquaintanceSubmit)
  .buildWithProps({
    sx: { width: { xs: '100%', [MOBILE_BREAKPOINT_THRESHOLD]: 'auto' } },
    type: 'submit',
  });

const StringControl = componentBuilder()
  .compose(withErrorTooltip)
  .composeWithArgs(
    withTransformer,
    new StringTransformer({
      textCase: StringCase.SentenceCase,
    }),
  )
  .compose(withChangeableValue)
  .build(TextInput);

const BirthDateControl = componentBuilder()
  .compose(withErrorTooltip)
  .composeWithArgs(withInputMask, '##.##.####')
  .composeWithArgs(
    withTransformer,
    new DateTransformer({
      dateFormat: DateFormat.DateDefault,
    }),
  )
  .compose(withChangeableValue<string, HTMLInputElement>)
  .build(TextInput);

export const StepAcquaintance = ({ state, model, onSubmit }: ClientRegistrationStepProps) => {
  type FormValues = { state: Partial<typeof state> };
  const handleApiServerError = useErrorHandler();
  const form = useForm<FormValues>({
    defaultValues: model.fields.reduce(
      (acc, field) => {
        const name = field.name;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        acc.state[name] = state[name];
        return acc;
      },
      { state: {} } as FormValues,
    ),
    resolver: (values) => {
      const requiredNames = model.fields.filter((field) => field.required).map((field) => field.name);
      const errors = {};
      for (const [name, value] of Object.entries(values.state)) {
        if (!requiredNames.includes(name)) {
          continue;
        }
        if (!value) {
          errors[wrapFieldName(name)] = { message: 'Заполните поле' };
        }
      }

      return { values, errors };
    },
  });
  const { isValid, isSubmitting } = useFormState({ control: form.control });
  const handleSubmit: SubmitHandler<FormValues> = useCallback(
    async (v) => {
      try {
        await onSubmit(v.state);
      } catch (err) {
        handleApiServerError(err, { form });
      }
    },
    [onSubmit, handleApiServerError, form],
  );

  const trackAcquaintanceVisible = useTracking(acquaintancePopupVisible);
  useEffect(trackAcquaintanceVisible, [trackAcquaintanceVisible]);

  return (
    <AppFormProvider form={form}>
      <Box component="form" onSubmit={form.handleSubmit(handleSubmit)} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <StepWrapper sx={{ minHeight: 'auto' }}>
          <StepContainer sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {model.fields.map((config) => {
              if (config.name === 'promocode') {
                return <PromoAtRegistration key={config.name} name={wrapFieldName('promocode')} />;
              }
              if (config.name === 'birthDate') {
                return (
                  <FormField Control={BirthDateControl} label={config.label} name={wrapFieldName(config.name)} placeholder="дд.мм.гггг" />
                );
              }
              if (['name', 'surname'].includes(config.name)) {
                return <FormField Control={StringControl} key={config.name} label={config.label} name={wrapFieldName(config.name)} />;
              }
              return (
                <ExternalFields
                  fieldLabel={config.label}
                  key={config.name}
                  name={wrapFieldName(config.name)}
                  placeholder={config.label}
                  type={(config.controlType || 'text') as any}
                />
              );
            })}
          </StepContainer>
          <StepFooter>
            <SubmitButton
              className={cx(
                EButtonName.Next,
                EPageName.Registration,
                model.name,
                CLIENT_TYPE_TO_CLIENT_TYPE_NAME[state.clientType as ClientType],
              )}
              disabled={!isValid}
              loading={isSubmitting}
            >
              Сохранить и продолжить
            </SubmitButton>
            {/* <Disclaimer /> */}
          </StepFooter>
        </StepWrapper>
      </Box>
    </AppFormProvider>
  );
};
